// Modal lệnh dùng chung SCR-050 (Spec §12.2): một form cho create/rename/transfer/merge/close/open.
// Trường bắt buộc theo hành động: decisionReference chỉ rename/transfer/merge/close (1–100);
// reason bắt buộc cho create/open (≤500), tùy chọn cho các lệnh còn lại.
// Merge hai bước: form → MergeImpactPreview → nút xác nhận nêu rõ tên hành động.
// State form nằm trong modal (không re-render trang); conflict giữ nguyên dữ liệu form.

import { useEffect, useRef, useState } from "react";
import { Alert, Button, DatePicker, Form, Input, Modal, Select, Typography } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { describeAdminError, isVersionConflict, type AdminCommandError } from "../commandErrors";
import { MergeImpactPreview } from "./MergeImpactPreview";
import { isPastEffectiveDate } from "./dateRules";
import type { AdminNode } from "../adminTypes";
import type { ReloadConflictSnapshot } from "./AdminWorkspace";

export type AdminCommandKind = "create" | "rename" | "transfer" | "merge" | "close" | "open";

export type AdminCommandValues =
  | Readonly<{
      kind: "create";
      code: string;
      name: string;
      classification: string;
      parentId: string | null;
      validFrom: string;
      reason: string;
    }>
  | Readonly<{
      kind: "rename";
      newName: string;
      effectiveFrom: string;
      decisionReference: string;
      reason?: string | undefined;
    }>
  | Readonly<{
      kind: "transfer";
      newParentId: string | null;
      effectiveFrom: string;
      decisionReference: string;
      reason?: string | undefined;
    }>
  | Readonly<{
      kind: "merge";
      absorbedId: string;
      absorbedExpectedVersion: number;
      effectiveFrom: string;
      decisionReference: string;
      reason?: string | undefined;
    }>
  | Readonly<{
      kind: "close";
      effectiveFrom: string;
      decisionReference: string;
      reason?: string | undefined;
    }>
  | Readonly<{ kind: "open"; effectiveFrom: string; reason: string }>;

export type AdminCommandChoice = Readonly<{ id: string; code: string; name: string }>;

const DATE_FORMAT = "DD/MM/YYYY";

const TITLES: Record<AdminCommandKind, string> = {
  create: "Tạo mới",
  rename: "Đổi tên",
  transfer: "Chuyển quản lý",
  merge: "Sáp nhập",
  close: "Đóng",
  open: "Mở lại",
};

const OK_TEXT: Record<AdminCommandKind, string> = {
  create: "Tạo mới",
  rename: "Đổi tên",
  transfer: "Chuyển quản lý",
  merge: "Xem ảnh hưởng",
  close: "Đóng",
  open: "Mở lại",
};

type FormValues = {
  code?: string | undefined;
  name?: string | undefined;
  classification?: string | undefined;
  validFrom?: Dayjs | undefined;
  newName?: string | undefined;
  effectiveFrom?: Dayjs | undefined;
  newParentId?: string | undefined;
  absorbedId?: string | undefined;
  decisionReference?: string | undefined;
  reason?: string | undefined;
};

function optionalText(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

export function AdminCommandModal({
  open,
  kind,
  target,
  createParentId,
  classificationLabel,
  entityNoun,
  choices,
  initialValues,
  submitting,
  error,
  childrenOf,
  resolveVersion,
  onReloadConflictSnapshot,
  onCancel,
  onSubmit,
}: {
  readonly open: boolean;
  readonly kind: AdminCommandKind;
  readonly target: AdminCommandChoice | null;
  readonly createParentId: string | null;
  readonly classificationLabel: string;
  readonly entityNoun: string;
  readonly choices: readonly AdminCommandChoice[];
  readonly initialValues: Readonly<{ newName?: string | undefined }>;
  readonly submitting: boolean;
  readonly error: AdminCommandError | null;
  readonly childrenOf: (id: string) => readonly AdminNode[];
  readonly resolveVersion: (id: string) => Promise<number>;
  readonly onReloadConflictSnapshot: ReloadConflictSnapshot;
  readonly onCancel: () => void;
  readonly onSubmit: (values: AdminCommandValues) => void;
}) {
  const [form] = Form.useForm<FormValues>();
  const [mergeStep, setMergeStep] = useState<"form" | "preview">("form");
  const [absorbedVersion, setAbsorbedVersion] = useState<number | null>(null);
  const [resolveError, setResolveError] = useState<string | null>(null);
  const [reloadingConflict, setReloadingConflict] = useState(false);
  const initialValuesRef = useRef(initialValues);
  initialValuesRef.current = initialValues;

  // Reset imperative chỉ khi MỞ modal (modal mount mới cho mỗi lệnh). KHÔNG phụ thuộc
  // identity của initialValues — refresh nền sau conflict có thể làm prop đổi chỗ.
  useEffect(() => {
    if (open) {
      form.resetFields();
      if (kind === "rename") form.setFieldsValue({ newName: initialValuesRef.current.newName });
      setMergeStep("form");
      setAbsorbedVersion(null);
      setResolveError(null);
    }
  }, [open, kind, form]);

  const disabledDate = (current: Dayjs | null) => isPastEffectiveDate(current, dayjs().startOf("day"));
  const optionList = choices.map((choice) => ({
    value: choice.id,
    label: `${choice.name} (${choice.code})`,
  }));
  const requiresReference = kind === "rename" || kind === "transfer" || kind === "merge" || kind === "close";
  const reasonRequired = kind === "create" || kind === "open";

  const buildValues = (values: FormValues): AdminCommandValues => {
    const effectiveFrom = (kind === "create" ? values.validFrom : values.effectiveFrom)?.format("YYYY-MM-DD") ?? "";
    const reason = optionalText(values.reason);
    switch (kind) {
      case "create":
        return {
          kind,
          code: values.code as string,
          name: values.name as string,
          classification: values.classification as string,
          parentId: createParentId,
          validFrom: effectiveFrom,
          reason: reason as string,
        };
      case "rename":
        return {
          kind,
          newName: values.newName as string,
          effectiveFrom,
          decisionReference: values.decisionReference as string,
          reason,
        };
      case "transfer":
        return {
          kind,
          newParentId: values.newParentId ?? null,
          effectiveFrom,
          decisionReference: values.decisionReference as string,
          reason,
        };
      case "merge":
        return {
          kind,
          absorbedId: values.absorbedId as string,
          absorbedExpectedVersion: absorbedVersion as number,
          effectiveFrom,
          decisionReference: values.decisionReference as string,
          reason,
        };
      case "close":
        return {
          kind,
          effectiveFrom,
          decisionReference: values.decisionReference as string,
          reason,
        };
      case "open":
        return { kind, effectiveFrom, reason: reason as string };
    }
  };

  const handleSubmit = async () => {
    let values: FormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    if (kind === "merge" && mergeStep === "form") {
      try {
        setAbsorbedVersion(await resolveVersion(values.absorbedId as string));
        setResolveError(null);
        setMergeStep("preview");
      } catch {
        setResolveError("Không đọc được phiên bản hiện tại của đơn vị bị sáp nhập, thử lại.");
      }
      return;
    }
    onSubmit(buildValues(values));
  };

  const reloadConflict = async () => {
    const absorbedIdValue: unknown = kind === "merge" ? form.getFieldValue("absorbedId") : null;
    const absorbedId = typeof absorbedIdValue === "string" ? absorbedIdValue : null;
    setReloadingConflict(true);
    try {
      const snapshot = await onReloadConflictSnapshot(absorbedId);
      if (kind === "merge" && snapshot.absorbedVersion == null) {
        throw new Error("Absorbed version is required for merge retry");
      }
      setAbsorbedVersion(snapshot.absorbedVersion);
      setResolveError(null);
    } catch {
      setResolveError("Không tải lại được phiên bản mới nhất, vui lòng thử lại.");
    } finally {
      setReloadingConflict(false);
    }
  };

  const absorbedChoice = choices.find((choice) => choice.id === form.getFieldValue("absorbedId"));
  const mergeConfirmText =
    target && absorbedChoice
      ? `Sáp nhập "${absorbedChoice.name}" vào "${target.name}"`
      : "Sáp nhập";

  const conflictAlert = error && (
    <Alert
      type="error"
      showIcon
      style={{ marginBottom: 16 }}
      title={describeAdminError(error.code)}
      description={error.requestId ? `Mã yêu cầu: ${error.requestId}` : undefined}
      action={
        isVersionConflict(error.code) ? (
          <Button size="small" loading={reloadingConflict} onClick={() => void reloadConflict()}>
            Tải lại phiên bản
          </Button>
        ) : undefined
      }
    />
  );

  return (
    <Modal
      open={open}
      title={
        kind === "create" && target
          ? `Tạo ${entityNoun} trực thuộc "${target.name}"`
          : kind === "create"
            ? `Tạo ${entityNoun} cấp gốc`
            : TITLES[kind]
      }
      onCancel={onCancel}
      mask={{ closable: false }}
      footer={
        kind === "merge" && mergeStep === "preview"
          ? [
              <Button key="back" onClick={() => setMergeStep("form")}>
                Quay lại
              </Button>,
              <Button key="confirm" type="primary" disabled={reloadingConflict} loading={submitting} onClick={() => void handleSubmit()}>
                {mergeConfirmText}
              </Button>,
            ]
          : undefined
      }
      okText={OK_TEXT[kind]}
      cancelText="Hủy"
      okButtonProps={{ disabled: reloadingConflict, loading: submitting }}
      onOk={() => void handleSubmit()}
    >
      {conflictAlert}
      {resolveError && (
        <Alert type="error" showIcon style={{ marginBottom: 16 }} title={resolveError} />
      )}
      <div style={{ display: kind === "merge" && mergeStep === "preview" ? "none" : "block" }}>
        {target && kind !== "create" && (
          <Typography.Paragraph type="secondary">
            Đối tượng: {target.name} ({target.code})
          </Typography.Paragraph>
        )}
        <Form form={form} layout="vertical" preserve={false}>
          {kind === "create" && (
            <>
              <Form.Item
                name="code"
                label="Mã"
                rules={[
                  { required: true, message: "Bắt buộc nhập mã" },
                  { max: 50, message: "Mã tối đa 50 ký tự" },
                ]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="name"
                label="Tên"
                rules={[
                  { required: true, message: "Bắt buộc nhập tên" },
                  { max: 200, message: "Tên tối đa 200 ký tự" },
                ]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="classification"
                label={classificationLabel}
                extra="Giá trị theo danh mục dùng chung"
                rules={[{ required: true, message: `Bắt buộc nhập ${classificationLabel.toLowerCase()}` }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="validFrom"
                label="Ngày hiệu lực"
                rules={[{ required: true, message: "Bắt buộc chọn ngày hiệu lực" }]}
              >
                <DatePicker format={DATE_FORMAT} disabledDate={disabledDate} style={{ width: "100%" }} />
              </Form.Item>
            </>
          )}
          {kind === "rename" && (
            <Form.Item
              name="newName"
              label="Tên mới"
              rules={[
                { required: true, message: "Bắt buộc nhập tên mới" },
                { max: 200, message: "Tên tối đa 200 ký tự" },
              ]}
            >
              <Input />
            </Form.Item>
          )}
          {kind === "transfer" && (
            <Form.Item
              name="newParentId"
              label="Đơn vị quản lý mới"
              extra="Bỏ trống để chuyển về cấp gốc"
            >
              <Select allowClear showSearch optionFilterProp="label" placeholder="Chọn đơn vị quản lý mới" options={optionList} />
            </Form.Item>
          )}
          {kind === "merge" && (
            <Form.Item
              name="absorbedId"
              label="Đơn vị bị sáp nhập"
              rules={[{ required: true, message: "Chọn đơn vị bị sáp nhập" }]}
            >
              <Select showSearch optionFilterProp="label" placeholder="Chọn đơn vị bị sáp nhập" options={optionList} />
            </Form.Item>
          )}
          {kind !== "create" && (
            <Form.Item
              name="effectiveFrom"
              label="Ngày hiệu lực"
              rules={[{ required: true, message: "Bắt buộc chọn ngày hiệu lực" }]}
            >
              <DatePicker format={DATE_FORMAT} disabledDate={disabledDate} style={{ width: "100%" }} />
            </Form.Item>
          )}
          {requiresReference && (
            <Form.Item
              name="decisionReference"
              label="Mã căn cứ quyết định"
              rules={[
                { required: true, message: "Bắt buộc nhập mã căn cứ quyết định" },
                { min: 1, message: "Mã căn cứ tối thiểu 1 ký tự" },
                { max: 100, message: "Mã căn cứ tối đa 100 ký tự" },
              ]}
            >
              <Input />
            </Form.Item>
          )}
          <Form.Item
            name="reason"
            label="Lý do"
            rules={
              reasonRequired
                ? [
                    { required: true, message: "Bắt buộc nhập lý do" },
                    { max: 500, message: "Lý do tối đa 500 ký tự" },
                  ]
                : [{ max: 500, message: "Lý do tối đa 500 ký tự" }]
            }
          >
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </div>
      {kind === "merge" && mergeStep === "preview" && target && absorbedChoice && (
        <MergeImpactPreview
          survivor={target}
          absorbed={absorbedChoice}
          rehomedChildren={childrenOf(absorbedChoice.id)}
        />
      )}
    </Modal>
  );
}
